import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { bottomNavLinks, getNavLinksForModule } from "./links";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../store/slices/authSlice";
import { selectActiveModuleId } from "../../store/slices/moduleSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Images from "../../utils/Images";

export default function Sidebar({
  isOpen,
  onClose,
  isCollapsed,
  onToggleCollapse,
}) {
  const [expandedItems, setExpandedItems] = useState([]);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeModuleId = useAppSelector(selectActiveModuleId);
  
  // Get navigation links for the active module
  const navLinks = useMemo(() => {
    return getNavLinksForModule(activeModuleId);
  }, [activeModuleId]);

  const toggleExpanded = (path) => {
    setExpandedItems((prev) =>
      prev.includes(path)
        ? prev.filter((item) => item !== path)
        : [...prev, path]
    );
  };

  const handleLogout = async () => {
    const result = dispatch(logout());
    if (logout.fulfilled.match(result)) {
      toast.success("Logged out successfully!");
      navigate("/signin", { replace: true });
    }
  };

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const isExpanded = (path) => expandedItems.includes(path);

  const NavItem = ({ item, level = 0 }) => {
    const hasChildren = item.children && item.children.length > 0;
    const active = isActive(item.path);
    const expanded = isExpanded(item.path);

    return (
      <div>
        <Link
          to={hasChildren ? "#" : item.path}
          onClick={(e) => {
            if (hasChildren) {
              e.preventDefault();
              if (!isCollapsed) {
                toggleExpanded(item.path);
              }
            }
          }}
          className={`
            group relative flex items-center justify-between px-3.5 py-2.5 text-sm font-medium 
            transition-all duration-300 rounded-xl mx-2 overflow-hidden
            ${
              active
                ? level > 0
                  ? "text-emerald-700 bg-gradient-to-r from-emerald-50 to-emerald-100 shadow-md shadow-emerald-200/30 border border-emerald-200"
                  : "text-white bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/30"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80"
            }
            ${level > 0 && !isCollapsed ? "ml-6" : ""} 
            ${isCollapsed ? "justify-center px-2" : ""} 
            ${hasChildren ? "cursor-pointer" : ""}
          `}
          title={isCollapsed ? item.title : undefined}
        >
          {/* Active indicator line */}
          {active && !isCollapsed && (
            <div className={`absolute top-0 bottom-0 left-0 w-1 rounded-r-full ${
              level > 0 ? "bg-emerald-500" : "bg-white"
            }`} />
          )}

          <div
            className={`flex items-center ${
              isCollapsed ? "justify-center" : "space-x-3"
            } z-10`}
          >
            <motion.div
              whileHover={{ scale: 1.1, rotate: active ? 0 : 5 }}
              transition={{ type: "spring", stiffness: 400 }}
              className="flex-shrink-0"
            >
              <item.icon
                size={20}
                className={`transition-colors ${
                  active
                    ? level > 0
                      ? "text-emerald-600"
                      : "text-white"
                    : "text-gray-500 group-hover:text-emerald-600"
                }`}
              />
            </motion.div>
            {!isCollapsed && (
              <span className="font-semibold tracking-wide truncate">
                {item.title}
              </span>
            )}
          </div>

          {!isCollapsed && (
            <div className="flex z-10 flex-shrink-0 items-center space-x-2">
              {item.badge && (
                <span
                  className={`px-2.5 py-0.5 text-xs font-bold rounded-full whitespace-nowrap shadow-sm ${
                    active
                      ? level > 0
                        ? "text-emerald-700 bg-emerald-200"
                        : "text-emerald-600 bg-white"
                      : "text-gray-700 bg-gray-200/80"
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {hasChildren && (
                <motion.div
                  className="flex-shrink-0"
                  animate={{ rotate: expanded ? 90 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <ChevronRight
                    size={16}
                    className={active ? (level > 0 ? "text-emerald-600" : "text-white") : "text-gray-400"}
                  />
                </motion.div>
              )}
            </div>
          )}

          {/* Hover effect background */}
          {!active && (
            <div className="absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 from-emerald-500/5 to-emerald-600/5 group-hover:opacity-100" />
          )}
        </Link>

        <AnimatePresence>
          {hasChildren && !isCollapsed && expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="mt-1 space-y-0.5 pb-1">
                {item.children.map((child, index) => (
                  <NavItem
                    key={`${item.path}-${index}`}
                    item={child}
                    level={level + 1}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[55] backdrop-blur-sm bg-black/60 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0 z-[60] h-screen bg-white/95 backdrop-blur-xl
          border-r border-gray-200/80 shadow-2xl shadow-gray-900/10
          transition-all duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:static lg:z-auto
          ${isCollapsed ? "w-16" : "w-80"}
        `}
      >
        {/* Header */}
        <div className="relative flex justify-between items-center p-4 border-b border-gray-200/80 min-h-[64px] bg-gradient-to-r from-gray-50/50 to-transparent">
          <div className="flex items-center space-x-3">
            {!isCollapsed && (
              <Link
                to="/admin/dashboard"
                className="block transition-transform hover:scale-105"
              >
                <img
                  src={Images.logo_colored}
                  alt="TripSecure Admin"
                  className="w-48"
                />
              </Link>
            )}
            {isCollapsed && (
              <Link to="/admin/dashboard" className="block">
                <motion.img
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                  src={Images.logo_white}
                  alt="TripSecure Admin"
                  className="w-8 h-8"
                />
              </Link>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Collapse Toggle - Desktop Only */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onToggleCollapse}
              className="hidden p-2 text-gray-400 rounded-xl transition-all hover:bg-emerald-50 hover:text-emerald-600 lg:block"
              aria-label="Toggle sidebar"
            >
              <ChevronLeft
                size={18}
                className={`transition-transform duration-300 ${
                  isCollapsed ? "rotate-180" : ""}`}
              />
            </motion.button>
            {/* Close Button - Mobile Only */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="block p-2 text-gray-400 rounded-xl transition-all lg:hidden hover:bg-red-50 hover:text-red-600"
              aria-label="Close sidebar"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col h-[calc(100vh-64px)]">
          {/* Main Navigation */}
          <nav
            className="overflow-y-auto flex-1 px-2 py-4 space-y-1.5 
            scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 
            scrollbar-track-transparent scrollbar-thumb-rounded-full"
          >
            {navLinks.map((item, index) => (
              <NavItem key={index} item={item} />
            ))}
          </nav>

          {/* Bottom Navigation */}
          <div className="flex-shrink-0 px-2 py-2 pb-8 bg-gradient-to-t to-transparent border-t from-gray-50/50 border-gray-200/80">
            <div className="space-y-1">
              {bottomNavLinks.map((item, index) => {
                const isLogout = item.path === "/logout";
                const isActiveBottom = isActive(item.path);

                return (
                  <motion.div
                    key={index}
                    whileHover={{ x: isCollapsed ? 0 : 2 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Link
                      to={isLogout ? "#" : item.path}
                      onClick={isLogout ? handleLogout : undefined}
                      className={`
                        group relative flex items-center px-3.5 py-2 text-sm font-medium 
                        transition-all duration-300 rounded-xl overflow-hidden
                        ${
                          isActiveBottom
                            ? "text-emerald-700 bg-emerald-50 shadow-md"
                            : isLogout
                            ? "text-red-600 hover:text-red-700 hover:bg-red-50/80"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80"
                        }
                        ${isCollapsed ? "justify-center px-2" : "space-x-3"}
                      `}
                      title={isCollapsed ? item.title : undefined}
                    >
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: isLogout ? 0 : 5 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <item.icon
                          size={20}
                          className={`flex-shrink-0 transition-colors ${
                            isActiveBottom
                              ? "text-emerald-600"
                              : isLogout
                              ? "group-hover:text-red-600"
                              : "group-hover:text-emerald-600"
                          }`}
                        />
                      </motion.div>
                      {!isCollapsed && (
                        <span className="font-semibold tracking-wide">
                          {item.title}
                        </span>
                      )}

                      {/* Hover effect */}
                      {!isActiveBottom && (
                        <div
                          className={`absolute inset-0 ${
                            isLogout
                              ? "bg-gradient-to-r from-red-500/5 to-red-600/5"
                              : "bg-gradient-to-r from-emerald-500/5 to-emerald-600/5"
                          } opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
